import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Gem, SlidersHorizontal, Drill, Leaf } from "lucide-react";
import { FadeUp, Eyebrow } from "./motion";
import { useContent } from "../lib/content";

const BENEFITS = [
  { icon: Gem, title: "Premium materials", desc: "High-quality fabrics and accessories combining durability, elegance and function." },
  { icon: SlidersHorizontal, title: "Customized solutions", desc: "Designed around your interior style, window dimensions and preferences." },
  { icon: Drill, title: "Professional installation", desc: "Experienced installers ensure precision, perfect fitting and flawless finishing." },
  { icon: Leaf, title: "Hassle-free experience", desc: "From consultation to final installation, we handle every detail professionally." },
];

function CountUp({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  const decimals = Number.isInteger(Number(to)) ? 0 : 1;

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, Number(to), {
      duration: 2,
      ease: "easeOut",
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} className="font-serif text-4xl sm:text-5xl text-reef">
      {val.toFixed(decimals)}
      <span className="text-reef/60">{suffix}</span>
    </span>
  );
}

export default function WhyUs() {
  const { story, stats } = useContent();

  return (
    <section id="story" data-testid="story-section" className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-6">
            <Eyebrow className="mb-4">{story.eyebrow}</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-slate-900">
              {story.headline1} <span className="italic text-reef">{story.headline_accent}</span> {story.headline2}
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              {story.body}
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {["Premium quality", "Smart pricing", "Professional service", "Budget-friendly", "Hassle-free"].map((t) => (
                <span
                  key={t}
                  className="px-4 py-2 rounded-full border border-reef/25 text-reef font-mono text-[11px] tracking-[0.12em] uppercase"
                >
                  {t}
                </span>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="relative max-w-md mx-auto lg:ml-auto">
              <div className="absolute -inset-3 border border-reef/20 rounded-[28px] rotate-2" />
              <div className="relative overflow-hidden rounded-[28px] aspect-[4/3] border border-sand-border">
                <img src={story.image} alt="Curated linen fabric swatches" loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-3 sm:-right-6 bg-reef text-sand-bg rounded-2xl px-6 py-4 shadow-xl">
                <p className="font-serif text-3xl leading-none">{story.badge_value}</p>
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase mt-1.5 text-sand-bg/80">
                  {story.badge_label}
                </p>
              </div>
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-20 lg:mt-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 border-y border-sand-border py-10">
            {stats.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-center px-2">
                <CountUp to={s.to} suffix={s.suffix} />
                <p className="mt-2 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-slate-500">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b, i) => (
            <FadeUp key={b.title} delay={i * 0.08}>
              <div
                data-testid={`benefit-card-${i}`}
                className="h-full bg-white border border-sand-border rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:border-reef/40"
              >
                <div className="w-11 h-11 rounded-xl bg-reef-light flex items-center justify-center">
                  <b.icon className="w-5 h-5 text-reef" strokeWidth={1.8} />
                </div>
                <h3 className="mt-4 font-serif text-lg text-slate-900">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
